import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  computeRoi,
  formatRoiMoney,
} from "../utils/roiCalculations";

const SLIDER_MAX = 400;
const NUM_MAX = 2000;
const DEFAULT_BEDS = 100;

const ROICalculator: React.FC = () => {
  const [beds, setBeds] = useState(DEFAULT_BEDS);

  const clampedBeds = useMemo(() => {
    const n = Math.round(beds);
    if (!Number.isFinite(n) || n < 1) return 1;
    return Math.min(NUM_MAX, n);
  }, [beds]);

  const result = useMemo(() => computeRoi(clampedBeds), [clampedBeds]);

  const syncFromSlider = (v: number) => setBeds(v);

  const syncFromNumber = (raw: number) => {
    const v = Math.max(1, Math.min(NUM_MAX, Math.round(raw) || 1));
    setBeds(v);
  };

  return (
    <div className="mx-auto mb-10 max-w-[640px] font-sans">
      <div className="rounded-[14px] bg-slate-100 p-6">
        <h2 className="mb-1.5 text-[22px] font-semibold leading-tight text-gray-900">
          Calculate your ROI
        </h2>
        <p className="mb-5 text-[13px] leading-normal text-gray-500">
          Enter your facility&apos;s bed count to estimate how quickly
          RoomRounds pays for itself.
        </p>

        <div className="rounded-[10px] border border-gray-200 bg-white p-5">
          <label
            htmlFor="rr-beds"
            className="mb-2 block text-[13px] font-semibold text-gray-800"
          >
            Number of beds
          </label>
          <div className="mb-2 flex items-center gap-3.5">
            <input
              id="rr-beds"
              type="range"
              min={1}
              max={SLIDER_MAX}
              step={1}
              value={Math.min(clampedBeds, SLIDER_MAX)}
              onChange={(e) => syncFromSlider(+e.target.value)}
              aria-label="Bed count slider"
              className="h-1 flex-1 cursor-pointer accent-blue-800"
            />
            <input
              id="rr-beds-num"
              type="number"
              min={1}
              max={NUM_MAX}
              value={clampedBeds}
              onChange={(e) => syncFromNumber(+e.target.value)}
              aria-label="Bed count"
              className="h-[38px] w-[88px] rounded-lg border border-gray-300 text-center text-[15px] text-gray-900 focus:border-blue-800 focus:outline-none focus:ring-[3px] focus:ring-blue-800/10"
            />
          </div>
          <div className="mb-4 min-h-4 text-xs text-gray-500">
            {result.tierName}
          </div>

          <div className="border-t border-gray-200 pt-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg bg-slate-50 p-3.5">
                <div className="mb-1 text-xs text-gray-500">
                  Hours saved / week
                </div>
                <div className="text-2xl font-semibold text-blue-800">
                  ~{Math.round(result.savedWk)} hrs
                </div>
              </div>
              <div className="rounded-lg bg-slate-50 p-3.5">
                <div className="mb-1 text-xs text-gray-500">
                  Monthly savings (est.)
                </div>
                <div className="text-2xl font-semibold text-green-700">
                  {formatRoiMoney(result.monthlySavings)}
                </div>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div className="rounded-lg bg-slate-50 p-3.5">
                <div className="mb-1 text-xs text-gray-500">Monthly price</div>
                <div
                  className={`font-semibold text-gray-900 ${
                    result.enterprise ? "text-lg" : "text-2xl"
                  }`}
                >
                  {result.enterprise
                    ? "Upon request"
                    : formatRoiMoney(result.monthlyPrice!)}
                </div>
              </div>
              <div className="rounded-lg bg-slate-50 p-3.5">
                <div className="mb-1 text-xs text-gray-500">Payback time</div>
                <div className="text-2xl font-semibold text-gray-900">
                  {result.enterprise
                    ? "—"
                    : `~${result.paybackDays!.toFixed(1)} days`}
                </div>
              </div>
            </div>

            {result.enterprise && (
              <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 px-3.5 py-3 text-[13px] leading-normal text-blue-900">
                <strong>Enterprise scale (201+ beds).</strong> Pricing is custom
                for facilities this size —{" "}
                <Link to="/contact" className="underline hover:text-blue-700">
                  contact us for a quote
                </Link>
                . Your estimated savings are shown above.
              </div>
            )}
          </div>
        </div>

        <p className="mt-3 px-0.5 text-[11px] italic leading-relaxed text-gray-400">
          Estimates based on 40% documented efficiency improvement (LeadingAge
          2024), a blended labor rate of $50/hr, and ~1.2 manual rounding
          hrs/bed/week. Price reflects 50% assumed adoption of maintenance
          &amp; support modules. Results vary by facility workflow, staffing
          model, and adoption.
        </p>
      </div>
    </div>
  );
};

export default ROICalculator;
